import {
  type InfiniteData,
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type PaginatedResponse } from "@/shared/http";

interface UseInfiniteQueryResultsOptions {
  pageSize?: number;
}

/**
 * Walks the cursor chain via `useInfiniteQuery`, accumulating pages and
 * exposing `data` as a single flat-mapped array of items. For "load more"
 * UIs that want all loaded results concatenated (e.g., `Search/SearchResults`).
 *
 * For paginated table views that show one page at a time, use
 * `useQueryResults` instead — it keys per page so backward navigation reads
 * directly from cache.
 */
export function useInfiniteQueryResults<T>(
  queryId: string | undefined,
  opts: UseInfiniteQueryResultsOptions = {},
): UseInfiniteQueryResult<T[], Error> {
  const { queryService } = useDomainServices();
  const { pageSize = 50 } = opts;

  return useInfiniteQuery({
    queryKey: ["queryResults", queryId, pageSize, "infinite"],
    queryFn: ({ pageParam, signal }) => {
      if (queryId === undefined) {
        return Promise.reject(new Error("Invalid queryId"));
      }

      return queryService.getQueryResults<T>(queryId, {
        abortSignal: signal,
        nextPageToken: pageParam,
        pageSize,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage: PaginatedResponse<T>) => lastPage.next_token,
    select: (data: InfiniteData<PaginatedResponse<T>>) =>
      data.pages.flatMap((page) => page.items),
    enabled: queryId !== undefined,
    staleTime: Infinity,
  });
}
